import React, { useState } from 'react';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { useDispatch } from 'react-redux';
import { SelectableList } from 'components/UI';
import i18n from 'i18n-js';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';
import { uiActions } from 'state/modules/UI';

import useStyles from './Locations.styled';
import useDeepCompareEffect from 'use-deep-compare-effect';

const filteredLocations = (locations = [], assignedLocations = []) => {
  const filteredArray = locations.map((location) => {
    if (assignedLocations?.length === 0) {
      location.disabled = false;
      location.selected = false;
      return location;
    } else {
      for (let i = 0; i < assignedLocations.length; i++) {
        if (assignedLocations[i]?.id === location.id) {
          location.disabled = true;
        }
      }
      return location;
    }
  });
  return filteredArray;
};

const locationsColumns = [
  {
    name: 'Zone',
    selector: 'name',
    sortable: false,
  },
];

const AvailablesLocations = ({
  data = [],
  handlePageChange,
  handlePerRowsChange,
  loading,
  onRowClicked,
  totalRows = 0,
  paginationDefaultPage,
  paginationPerPage,
}) => {
  return (
    <SelectableList
      mainTable={true}
      dataCy="available-list"
      columns={locationsColumns}
      data={data}
      loading={loading}
      handlePageChange={handlePageChange}
      onRowClicked={onRowClicked}
      handlePerRowsChange={handlePerRowsChange}
      title={i18n.t('vehicle_groups.available_locations')}
      totalRows={totalRows}
      paginationPerPage={paginationPerPage}
      paginationDefaultPage={paginationDefaultPage}
    />
  );
};

const assignedColumns = [
  {
    name: 'Zone',
    selector: 'name',
    sortable: false,
  },
];

const SelectedLocations = ({
  data = [],
  loading = false,
  onRowClicked,
  handlePageChange,
  handlePerRowsChange,
  totalRows,
  paginationPerPage,
  paginationDefaultPage,
}) => {
  return (
    <SelectableList
      dataCy="selected-list"
      columns={assignedColumns}
      data={data}
      loading={loading}
      onRowClicked={onRowClicked}
      handlePageChange={handlePageChange}
      handlePerRowsChange={handlePerRowsChange}
      totalRows={totalRows}
      paginationPerPage={paginationPerPage}
      paginationDefaultPage={paginationDefaultPage}
      title={i18n.t('vehicle_groups.selected_locations')}
    />
  );
};

const VehiclesGroupsLocations = ({
  currentGroup,
  step = {},
  loading,
  type,
  parentGroup,
  showAvailableZoneText = true,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [availableParams, setAvailableParams] = useState(() => {
    const page = 1;
    const limit = 10;
    return { page, limit };
  });
  const [assignedParams, setAssignedParams] = useState(() => {
    const page = 1;
    const limit = 10;
    return { page, limit };
  });
  const [assignedLocations, setAssignedLocations] = useState([]);
  const [availablesLocations, setAvailablesLocations] = useState([]);
  const [selectedAssignedRow, setSelectedAssignedRow] = useState(null);
  const [selectedLocationRow, setSelectedAvailablesRow] = useState(null);

  const addLocationToVehicleGroup = () => {
    if (!selectedLocationRow?.id) {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: i18n.t('vehicle_groups.add_location_message'),
        })
      );
    }
    const payload = {
      location_type: type === 'service' ? 'MANAGED' : 'PRIMARY',
      location_id: selectedLocationRow?.id,
    };

    dispatch(VehiclesGroupsActions.fetchAddLocationToVehiclesGroups(currentGroup.id, payload));
  };

  const removeLocationFromVehicleGroup = () => {
    if (!selectedAssignedRow?.id) {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: i18n.t('vehicle_groups.remove_location_message'),
        })
      );
    }

    dispatch(
      VehiclesGroupsActions.fetchRemoveLocationFromVehiclesGroups(
        currentGroup.id,
        selectedAssignedRow.id
      )
    );
  };

  const fetchLocations = (params = {}) => {
    dispatch(VehiclesGroupsActions.fetchAvailableLocations(params));
  };

  const fetchVehiclesGroupsLocations = (id, type, params) => {
    dispatch(VehiclesGroupsActions.fetchVehiclesGroupsLocations(id, 'locations', type, params));
  };

  useDeepCompareEffect(() => {
    if (currentGroup?.id) {
      fetchVehiclesGroupsLocations(currentGroup.id, 'assigned', assignedParams);
    }
  }, [currentGroup, assignedParams]);

  useDeepCompareEffect(() => {
    if (parentGroup) {
      fetchVehiclesGroupsLocations(parentGroup, 'availables', availableParams);
    }

    if (type === 'service') {
      fetchLocations(availableParams);
    }
  }, [availableParams]); // eslint-disable-line

  useDeepCompareEffect(() => {
    if (step?.assigned?.list) {
      setAssignedLocations(step.assigned.list || []);
    }

    if (step?.availables?.list) {
      setAvailablesLocations(filteredLocations(step?.availables?.list, step?.assigned?.list) || []);
    }

    setSelectedAssignedRow(null);
    setSelectedAvailablesRow(null);
  }, [step]); // eslint-disable-line

  const handleAvailablePageChange = (page) =>
    setAvailableParams({ page, limit: availableParams.limit });
  const handleAssignedPageChange = (page) =>
    setAssignedParams({ page, limit: assignedParams.limit });

  const handleAvailablePerRowsChange = (limit) => setAvailableParams({ page: 1, limit });
  const handleAssignedPerRowsChange = (limit) => setAssignedParams({ page: 1, limit });

  return (
    <Box className={classes.container}>
      <Container className="h-100">
        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Grid item xs={12}>
            <Typography gutterBottom component="div">
              <Box fontWeight="fontWeightBold" data-cy="step-title" fontSize={14} my={2}>
                {showAvailableZoneText && i18n.t('vehicle_groups.select_available_zone')}
              </Box>
            </Typography>
            <Typography gutterBottom component="div">
              <Box fontSize={14} mb={4}>
                {i18n.t('vehicle_groups.location_description')}
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Grid container direction="row" alignItems="flex-start" justifyContent="space-around">
              <Grid item xs={5}>
                <AvailablesLocations
                  data={availablesLocations}
                  handlePageChange={handleAvailablePageChange}
                  handlePerRowsChange={handleAvailablePerRowsChange}
                  loading={step?.availables?.loading}
                  onRowClicked={setSelectedAvailablesRow}
                  totalRows={step?.availables?.pagination?.totalRows}
                  paginationDefaultPage={availableParams.page}
                  paginationPerPage={availableParams.limit}
                />
              </Grid>
              <Grid item xs={2}>
                <Box
                  display="flex"
                  alignItems="center"
                  flexDirection="column"
                  justifyContent="center"
                >
                  <Box py={1} px={2} style={{ width: '100%' }}>
                    <Button
                      disabled={loading || !selectedLocationRow}
                      onClick={addLocationToVehicleGroup}
                      className={classes.btn}
                      data-cy="add-location"
                      variant="outlined"
                      color="primary"
                      fullWidth
                    >
                      {i18n.t('vehicle_groups.add')}
                      <ArrowForwardIcon fontSize="inherit" />
                    </Button>
                  </Box>
                  {assignedLocations?.length > 0 && !step?.assigned?.loading && (
                    <Box py={1} px={2} style={{ width: '100%' }}>
                      <Button
                        disabled={loading || !selectedAssignedRow}
                        onClick={removeLocationFromVehicleGroup}
                        data-cy="remove-location"
                        className={classes.btn}
                        variant="outlined"
                        color="primary"
                        fullWidth
                      >
                        <ArrowBackIcon fontSize="inherit" />
                        {i18n.t('vehicle_groups.delete')}
                      </Button>
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid item xs={5}>
                <Box mb={5}>
                  <SelectedLocations
                    data={assignedLocations}
                    loading={step?.assigned?.loading}
                    onRowClicked={setSelectedAssignedRow}
                    handlePageChange={handleAssignedPageChange}
                    handlePerRowsChange={handleAssignedPerRowsChange}
                    totalRows={step?.assigned?.pagination?.totalRows}
                    paginationDefaultPage={assignedParams.page}
                    paginationPerPage={assignedParams.limit}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default VehiclesGroupsLocations;
